// src/taxcenter_ui.js
import { useEffect, useState } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import AutoFitText from './components/ui/AutoFitText';


const money = (n) => Number(n || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getQuarterlySalesTaxCells = (totals, meta) => [
  ['QUARTER', `${meta.quarter} ${meta.year}`],
  ['TOTAL PAYMENTS RECEIVED', money(totals.total_payments_received)],
  ['GROSS SALES (EXCLUDING SALES TAX)', money(totals.gross_sales)],
  ['TAXABLE SALES', money(totals.taxable_sales)],
  ['NON-TAXABLE / EXEMPT SALES', money(totals.nontaxable_sales)],
  ['STATE SALES TAX COLLECTED', money(totals.state_tax_collected)],
  ['COUNTY / LOCAL SALES TAX COLLECTED', money(totals.county_tax_collected)],
  ['TOTAL SALES TAX COLLECTED', money(totals.total_tax_collected)]
];

const getVehicleReportRows = (data) => (
  (Array.isArray(data?.by_vehicle) ? data.by_vehicle : []).map((row) => {
    const details = [row.year, row.make, row.model].filter(Boolean).join(' ');
    return {
      vehicle: row.vehicle_id == null
        ? 'UNASSIGNED / HISTORICAL'
        : `UNIT # ${row.unit_number}${details ? ` — ${details}` : ''}`,
      status: row.vehicle_id == null ? '—' : Number(row.is_active) === 1 ? 'ACTIVE' : 'INACTIVE',
      trips: row.trip_count,
      miles: `${Number(row.miles || 0).toFixed(2)} mi`,
      deduction: money(row.deductible_amount)
    };
  })
);

const getReportMileageRateDisplay = (data, type) => {
  const breakdown = type === 'mileage_summary'
    ? (Array.isArray(data?.rate_breakdown) ? data.rate_breakdown : [])
    : (Array.isArray(data?.mileage_rate_breakdown) ? data.mileage_rate_breakdown : []);
  const multiple = type === 'mileage_summary'
    ? data?.multiple_rates_applied === true
    : data?.multiple_mileage_rates === true;
  const directRate = data?.meta?.irs_rate_per_mile;
  const normalizedRate = directRate == null
    ? (breakdown.length === 1 ? Number(breakdown[0]?.rate) : null)
    : Number(directRate);
  const singleRate = Number.isFinite(normalizedRate) ? normalizedRate : null;

  return {
    breakdown,
    multiple,
    label: multiple
      ? 'Multiple IRS Rates Applied'
      : singleRate == null
        ? null
        : `IRS Rate: $${singleRate.toFixed(3)}/mile`
  };
};

const currentYear = new Date().getFullYear();

const TaxCenterUI = ({ navigate }) => {
  const isMobile = window.innerWidth < 1024;

  const API_BASE = getApiBase();

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const [businessName, setBusinessName] = useState('');

  const [quarterlyData, setQuarterlyData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const fetchNoCache = async (url) => {
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', ...getTenantHeaders() },
    });
    return res;
  };

  const fetchBusinessName = async () => {
    try {
      const res = await fetchNoCache(`${API_BASE}/api/settings/business`);
      if (!res.ok) return;
      const data = await res.json();
      const bn = String(data?.businessName || data?.business_name || '').trim();
      if (bn) setBusinessName(bn);
    } catch {}
  };

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const ts = Date.now();
      let res = await fetchNoCache(`${API_BASE}/api/taxcenter/report?year=${selectedYear}&ts=${ts}`);
      if (!res.ok) {
        res = await fetchNoCache(`${API_BASE}/taxcenter/report?year=${selectedYear}&ts=${ts}`);
      }
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setError(e.message || 'Failed to load report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReport();
    fetchQuarterly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const miles = Number(report?.mileage?.totalMiles || report?.mileage?.total_miles || 0);
  const mileageDeduction = Number(report?.mileage?.mileageDeduction || 0);
  const mileageRate = report?.mileage?.mileageRate;
  const normalizedMileageRate = mileageRate == null ? null : Number(mileageRate);
  const multipleRatesApplied = report?.mileage?.multipleRatesApplied === true;

  const gross = Number(report?.performance?.totalGrossRevenue || 0);
  const expenses = Number(report?.performance?.totalExpenses || 0);
  const netAfterExpenses = gross - expenses - mileageDeduction;

  const stateSalesTax = Number(report?.liabilities?.stateSalesTax || 0);
  const countySalesTax = Number(report?.liabilities?.countySalesTax || 0);
  const selfEmploymentTax = Number(report?.liabilities?.selfEmploymentTax || 0);
  const stateIncomeTax = Number(report?.liabilities?.stateIncomeTax || 0);
  const trueNetAfterTaxes = Number(report?.performance?.trueNetProfit || 0);

  const [hover, setHover] = useState({ refresh: false, back: false });
  const [rptData]    = useState(null);
  const [rptActive]  = useState(null);
  const [rptLoading] = useState(false);
  const [rptError]   = useState('');

  const fetchQuarterly = async () => {
    try {
      const ts = Date.now();
    const res = await fetchNoCache(`${API_BASE}/api/taxcenter/quarterly?year=${selectedYear}&ts=${ts}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.quarters)) setQuarterlyData(data.quarters);
    } catch (e) { console.error('fetchQuarterly failed:', e); }
  };

  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);

  const runReport = (reportId) => {
    navigate?.('settings', {
      settingsModule: 'reports',
      reportId
    });
  };

  const handleRptPrint = () => {
    if (!rptData) return;
    const t = rptData.totals || {};
    const m = rptData.meta   || {};
    const rptLabels = {
      schedule_c: 'SCHEDULE C', profit_loss: 'PROFIT & LOSS',
      quarterly_sales_tax: 'QUARTERLY SALES TAX',
      expenses_summary: 'EXPENSE SUMMARY', mileage_summary: 'MILEAGE SUMMARY',
    };
    const rptTitle = rptLabels[rptActive] || (rptActive || 'REPORT').toUpperCase();
    const dateHdr  = rptActive === 'quarterly_sales_tax'
      ? `${m.quarter || ''} ${m.year || selectedYear}`
      : `${selectedYear} (Jan 1 – Dec 31)`;
    const mileageRateDisplay = getReportMileageRateDisplay(rptData, rptActive);
    const mileageRateCell = mileageRateDisplay.label
      ? [['MILEAGE RATE', mileageRateDisplay.label]]
      : [];
    const printCells =
      rptActive === 'schedule_c'         ? [['GROSS RECEIPTS', money(t.gross_receipts)], ['TOTAL EXPENSES', money(t.total_expenses)], ['MILEAGE DEDUCTION', money(t.mileage_deduction)], ...mileageRateCell, ['NET PROFIT', money(t.net_profit)]] :
      rptActive === 'profit_loss'         ? [['TOTAL REVENUE', money(t.total_revenue)], ['TOTAL EXPENSES', money(t.total_expenses)], ['MILEAGE DEDUCTION', money(t.mileage_deduction)], ...mileageRateCell, ['NET PROFIT', money(t.net_profit)]] :
      rptActive === 'quarterly_sales_tax' ? getQuarterlySalesTaxCells(t, m) :
      rptActive === 'expenses_summary'    ? [['# OF EXPENSES', `${t.total_expenses}`], ['TOTAL AMOUNT', money(t.total_amount)]] :
      rptActive === 'mileage_summary'     ? [['TOTAL TRIPS', `${t.total_trips}`], ['TOTAL MILES', `${Number(t.total_miles || 0).toFixed(2)}`], ...mileageRateCell, ['DEDUCTIBLE AMOUNT', money(t.deductible_amount)]] :
      [];
    const buildTbl = (rows) => {
      if (!rows || !rows.length) return '';
      const cols = Object.keys(rows[0]);
      const hdr  = cols.map(c => `<th>${c.replace(/_/g,' ').replace(/\b\w/g,x=>x.toUpperCase())}</th>`).join('');
      const body = rows.map((r,i) => `<tr style="background:${i%2===0?'#fff':'#f5f5f5'}">${cols.map(c=>`<td>${r[c]??''}</td>`).join('')}</tr>`).join('');
      return `<table><thead><tr>${hdr}</tr></thead><tbody>${body}</tbody></table>`;
    };
    const chips  = printCells.map(([l,v]) => `<div class="chip"><strong>${v}</strong><br/><span style="font-size:10px;text-transform:uppercase">${l}</span></div>`).join('');
    const byCat  = Array.isArray(rptData.by_category) && rptData.by_category.length ? `<h3>BY CATEGORY</h3>${buildTbl(rptData.by_category)}` : '';
    const byVend = Array.isArray(rptData.by_vendor)   && rptData.by_vendor.length   ? `<h3>BY VENDOR</h3>${buildTbl(rptData.by_vendor)}`     : '';
    const detail = Array.isArray(rptData.rows)         && rptData.rows.length         ? `<h3>DETAIL (${rptData.rows.length} RECORDS)</h3>${buildTbl(rptData.rows)}` : '';
    const mileageBreakdownPrint = rptActive === 'mileage_summary' && mileageRateDisplay.breakdown.length
      ? `<h3>MILEAGE RATE BREAKDOWN</h3>${buildTbl(mileageRateDisplay.breakdown.map(row => ({
          'Effective Date': row.effectiveDate,
          'IRS Rate': `$${Number(row.rate).toFixed(3)}/mile`,
          'Miles': Number(row.miles).toFixed(2),
          'Deduction': money(row.deduction)
        })))}`
      : '';
    const byVehiclePrint = rptActive === 'mileage_summary' && getVehicleReportRows(rptData).length
      ? `<h3>BY VEHICLE</h3>${buildTbl(getVehicleReportRows(rptData))}`
      : '';
    const byStatePrint = Array.isArray(rptData.by_state) && rptData.by_state.length
      ? `<h3>BY STATE</h3>${buildTbl(rptData.by_state.map(r => ({ 'State': `${r.state_name} (${r.state_abbr})`, 'Invoices': r.invoice_count, 'State Tax': money(r.state_tax_collected), 'County Tax': money(r.county_tax_collected), 'Total Tax': money(r.total_tax_collected) })))}`
      : '';
    const byCountyPrint = Array.isArray(rptData.by_county) && rptData.by_county.length
      ? `<h3>BY COUNTY</h3>${buildTbl(rptData.by_county.map(r => ({ 'State': r.state_abbr, 'County': r.county_name, 'Rate': r.rate, 'Invoices': r.invoice_count, 'State Tax': money(r.state_tax_collected), 'County Tax': money(r.county_tax_collected), 'Total Tax': money(r.total_tax_collected) })))}`
      : '';
    const win = window.open('', '_blank');
    win.document.write(
      `<html><head><title>${rptTitle}</title><style>` +
      `@page{size:landscape;}` +
      `body{font-family:Arial Black,sans-serif;padding:24px;background:white;color:black;}` +
      `h1{font-size:20px;font-weight:900;text-transform:uppercase;margin-bottom:4px;}` +
      `.meta{font-size:12px;font-weight:700;color:#555;margin-bottom:16px;}` +
      `h3{font-size:13px;font-weight:900;text-transform:uppercase;border-bottom:2px solid black;padding-bottom:4px;margin:16px 0 8px;}` +
      `table{width:100%;border-collapse:collapse;font-size:9px;margin-bottom:16px;}` +
      `th{background:black;color:white;padding:7px;text-align:left;font-weight:900;text-transform:uppercase;font-size:10px;}` +
      `td{padding:6px 7px;border-bottom:1px solid #ccc;}` +
      `tr:nth-child(even) td{background:#f5f5f5;}` +
      `.chip{display:inline-block;border:2px solid black;padding:6px 12px;margin:4px;font-size:12px;font-weight:900;max-width:100%;box-sizing:border-box;overflow-wrap:anywhere;}` +
      `</style></head><body>` +
      `<div style="font-weight:900;font-size:22px;text-transform:uppercase;margin-bottom:4px;">${businessName || ''}</div>` +
      `<h1>${rptTitle}</h1><div class="meta">${dateHdr}</div>` +
      `<div style="margin-bottom:16px;">${chips}</div>` +
      mileageBreakdownPrint + byVehiclePrint + byStatePrint + byCountyPrint + byCat + byVend + detail +
      `</body></html>`
    );
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const title = `${(businessName || 'BUSINESS').toUpperCase()} | TAX CENTER`;

  const styles = {
    page: {
      padding: isMobile ? 10 : 18,
      background: '#E8E2DA',
      boxSizing: 'border-box',
      width: '100%',
    },

    // ── Title bar: navy outer, white fill, red+navy rounded inner ──
    titleOuter: {
      border: '8px solid #000000',
      background: '#ffffff',
      padding: 5,
      marginBottom: isMobile ? 10 : 14,
      boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
    },
    titleInner: {
      border: '4px solid #004d40',
      boxShadow: 'inset 0 0 0 3px #ffffff, inset 0 0 0 6px #4a0000',
      borderRadius: 10,
      background: '#ffffff',
      padding: isMobile ? '10px 16px' : '11px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      boxSizing: 'border-box',
    },
    titleLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
    iconBox: {
      width: 42,
      height: 42,
      border: '3px solid #000000',
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 22,
      background: '#f0f4ff',
    },
    titleText: {
      fontWeight: 900,
      fontSize: isMobile ? 18 : 26,
      color: '#000000',
      letterSpacing: 1,
    },
    subtitleText: {
      fontWeight: 700,
      fontSize: 11,
      color: '#c1121f',
      letterSpacing: 2,
      textTransform: 'uppercase',
      marginTop: 2,
    },
    titleRight: {
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      flexWrap: 'wrap',
      marginLeft: 'auto',
    },

    // ── Buttons ──
    btn: {
      background: '#0b1a3a',
      color: '#FFA500',
      border: '3px solid #FFA500',
      fontWeight: 900,
      padding: isMobile ? '9px 12px' : '9px 16px',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: 1,
      fontSize: 13,
      transition: 'all 140ms ease',
      whiteSpace: 'nowrap',
    },
    btnHover: {
      background: '#FFC107',
      color: '#000000',
      border: '3px solid #FFA500',
      transform: 'scale(1.02)',
    },

    // ── Cards ──
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: isMobile ? 10 : 14,
      maxWidth: '100%',
      overflow: 'hidden',
    },
    card: {
      border: '6px solid #ffffff',
      padding: isMobile ? 14 : 18,
      color: '#fff',
      minHeight: 240,
      boxSizing: 'border-box',
      minWidth: 0,
    },
    redCard: {
      background: '#4a0000',
      border: '6px solid #000000',
    },
    greenCard: {
      background: '#004d40',
      border: '6px solid #000000',
    },
    label: {
      fontWeight: 900,
      letterSpacing: 1,
      textTransform: 'uppercase',
      fontSize: 14,
      marginBottom: 8,
    },
    big: {
      fontWeight: 900,
      fontSize: isMobile ? '32px' : '42px',
      color: '#E8C98F',
      lineHeight: 1,
      marginBottom: 10,
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      whiteSpace: 'nowrap',
      textAlign: 'center',
    },
    small: {
      fontWeight: 900,
      opacity: 0.95,
      marginTop: 6,
      fontSize: 13,
      maxWidth: '100%',
      minWidth: 0,
      overflowWrap: 'normal',
      wordBreak: 'normal',
    },
    note: {
      marginTop: 12,
      fontWeight: 900,
      fontSize: 12,
      opacity: 0.9,
    },
    error: {
      marginTop: 12,
      padding: 12,
      border: '4px solid #000',
      background: '#fff',
      fontWeight: 900,
      color: '#b31217',
    },
  };

  return (
    <>
    <style>{`
      .jf-hover-blue { transition: filter 120ms ease, transform 120ms ease; }
      .jf-hover-blue:hover {
        background: #FFC107 !important;
        color: #000 !important;
        border-color: #FFA500 !important;
        filter: brightness(1.02);
      }
      .jf-hover-blue:active { transform: translateY(1px); }
    `}</style>
    <div style={styles.page}>
      {/* TITLE BAR */}
      <div style={styles.titleOuter}>
        <div style={styles.titleInner}>
          <div style={styles.titleLeft}>
            <div style={styles.iconBox}>🏛️</div>
            <div>
              <div style={styles.titleText}>{title}</div>
              <div style={styles.subtitleText}>FINANCIAL HEALTH</div>
            </div>
          </div>

          <div style={styles.titleRight}>
            <select
              style={{
                background: '#0b1a3a',
                color: '#FFA500',
                border: '3px solid #FFA500',
                fontWeight: 900,
                padding: isMobile ? '9px 12px' : '9px 16px',
                cursor: 'pointer',
                fontSize: 13,
                letterSpacing: 1,
              }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              title="Select Year"
            >
              {Array.from(
                { length: 10 },
                (_, i) => currentYear - 7 + i
              ).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              style={{ ...styles.btn, ...(hover.refresh ? styles.btnHover : {}) }}
              onMouseEnter={() => setHover((h) => ({ ...h, refresh: true }))}
              onMouseLeave={() => setHover((h) => ({ ...h, refresh: false }))}
              onClick={() => { fetchReport(); fetchQuarterly(); }}
              disabled={loading}
            >
              ⟳ REFRESH REPORT
            </button>
            <button
              style={{ ...styles.btn, ...(hover.back ? styles.btnHover : {}) }}
              onMouseEnter={() => setHover((h) => ({ ...h, back: true }))}
              onMouseLeave={() => setHover((h) => ({ ...h, back: false }))}
              onClick={() => navigate && navigate('dashboard')}
            >
              ← BACK TO DASHBOARD
            </button>
          </div>
        </div>
      </div>

      {error ? <div style={styles.error}>Tax Center failed to load: {error}</div> : null}

      {/* CARDS */}
      <div style={styles.grid}>
        {/* LEFT: Revenue + expenses + mileage */}
        <div style={{ ...styles.card, ...styles.greenCard }}>
          <div style={{ fontWeight: 900, fontSize: 15, color: '#ffffff', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 8, marginBottom: 12 }}>
            <div>📋 ANNUAL TOTALS</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#ffffff', letterSpacing: 1, marginTop: 3 }}>REVENUE & EXPENSES</div>
          </div>
          <div style={styles.label}>TOTAL REVENUE (POSTED PAYMENTS)</div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(gross)}</AutoFitText>

          <div style={{ ...styles.label, marginTop: 18 }}>TOTAL EXPENSES</div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(expenses)}</AutoFitText>

          <div style={{ ...styles.label, marginTop: 18 }}>MILEAGE DEDUCTION</div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(mileageDeduction)}</AutoFitText>
          <div style={styles.small}>{miles.toFixed(2)} miles recorded</div>
          {Number.isFinite(normalizedMileageRate) ? (
            <div style={styles.small}>IRS Rate: ${normalizedMileageRate.toFixed(3)}/mile</div>
          ) : multipleRatesApplied ? (
            <div style={styles.small}>Multiple IRS Rates Applied</div>
          ) : null}

          <div style={{ ...styles.label, marginTop: 18 }}>NET PROFIT (BEFORE TAXES)</div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(netAfterExpenses)}</AutoFitText>
          <div style={styles.note}>
            Note: Based on PAID invoices only. Dashboard reflects all invoices regardless of payment status.
          </div>
        </div>

        {/* RIGHT: Liabilities */}
        <div style={{ ...styles.card, ...styles.redCard }}>
          <div style={{ fontWeight: 900, fontSize: 15, color: '#ffffff', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 8, marginBottom: 12 }}>
            <div>📋 ANNUAL TOTALS</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#ffffff', letterSpacing: 1, marginTop: 3 }}>TAX LIABILITIES & NET TAKE HOME</div>
          </div>
          <div style={styles.label}>SALES TAX COLLECTED</div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(stateSalesTax + countySalesTax)}</AutoFitText>
          <div style={styles.small}>
            State: {money(stateSalesTax)} • County: {money(countySalesTax)}
          </div>

          <div style={{ ...styles.label, marginTop: 18 }}>
            <div style={{ color: '#E8C98F', marginBottom: 3 }}>ESTIMATED</div>
            <div>SELF-EMPLOYMENT TAX</div>
          </div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(selfEmploymentTax)}</AutoFitText>

          <div style={{ ...styles.label, marginTop: 18 }}>
            <div style={{ color: '#E8C98F', marginBottom: 3 }}>ESTIMATED</div>
            <div>STATE INCOME TAX</div>
          </div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(stateIncomeTax)}</AutoFitText>
          <div style={styles.small}>
            Rate: {Number(report?.rates?.stateIncomeTaxRate || 0).toFixed(2)}%
          </div>

          <div style={{ ...styles.label, marginTop: 18 }}>
            <div style={{ color: '#E8C98F', marginBottom: 3 }}>ESTIMATED</div>
            <div>NET PROFIT AFTER</div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginTop: 3 }}>
              (Expenses, Mileage, Taxes)
            </div>
          </div>
          <AutoFitText style={styles.big} maxFontSize={isMobile ? 32 : 42}>{money(trueNetAfterTaxes)}</AutoFitText>
          <div style={styles.note}>
            Note: Net profit reflects estimated self-employment and state income taxes. Collected sales tax is already excluded from posted-payment revenue.
          </div>
        </div>
      </div>

      {/* QUARTERLY TAX PAYMENT SCHEDULE */}
      <div style={{ marginTop: isMobile ? 10 : 14 }}>
        <div style={{
          fontWeight: 900,
          fontSize: isMobile ? 16 : 20,
          color: '#000000',
          letterSpacing: 2,
          textTransform: 'uppercase',
          border: '6px solid #000000',
          boxShadow: 'inset 0 0 0 3px #E8761D',
          paddingBottom: 8,
          marginBottom: 14,
          textAlign: 'center',
          backgroundColor: '#ffffff'
        }}>
          📊 QUARTERLY TAX PLANNING ESTIMATES 📊
          <div style={{ fontSize: 11, fontWeight: 700, color: '#000000', textAlign: 'center', marginTop: 4, marginBottom: 2, letterSpacing: 1, padding: '0 12px' }}>
            CALCULATED USING THE IRS ANNUALIZED INCOME INSTALLMENT METHOD
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#000000', textAlign: 'center', marginBottom: 4, letterSpacing: 1, padding: '0 12px' }}>
            ESTIMATES ARE BASED ON YOUR YEAR-TO-DATE INCOME, EXPENSES & MILEAGE
          </div>
          <div style={{ fontSize: 10, fontWeight: 900, color: '#000000', textAlign: 'center', marginBottom: 4, lineHeight: 1.4, padding: '0 12px' }}>
            * JANUARY 15th = JANUARY 15th OF THE FOLLOWING YEAR | ⚡ = CURRENT QUARTER
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
          {[
            { q: 1, label: 'Q1', range: 'Jan – Mar', seDue: 'Apr 15', stateDue: 'Apr 15', salesDue: '(Varies by State)' },
            { q: 2, label: 'Q2', range: 'Apr – Jun', seDue: 'Jun 15', stateDue: 'Jun 15', salesDue: '(Varies by State)' },
            { q: 3, label: 'Q3', range: 'Jul – Sep', seDue: 'Sep 15', stateDue: 'Sep 15', salesDue: '(Varies by State)' },
            { q: 4, label: 'Q4', range: 'Oct – Dec', seDue: 'Jan 15*', stateDue: 'Jan 15*', salesDue: '(Varies by State)' },
          ].map(({ q, label, range, seDue, stateDue }) => {
            const qRow = quarterlyData.find(r => r.quarter === q);
            const isCurrent = q === currentQuarter;
            const isPast = q < currentQuarter;
            const se    = Number(qRow?.se_tax    || 0);
            const state = Number(qRow?.state_tax || 0);
            const sales = Number(qRow?.sales_tax || 0);
            const fmt = (v) => qRow
              ? money(v)
              : isPast ? money(0) : 'TBD';

            const cardBorder = isCurrent ? '3px solid #E8C98F' : '3px solid #000000';
            const headerBg   = isCurrent ? '#004d40' : '#E7B888';
            const bodyBg     = isCurrent ? '#004d40' : '#ffffff';
            const amtColor   = isCurrent ? '#E8C98F' : '#754C38';
            const lblColor   = isCurrent ? '#ffffff' : '#000000';

            return (
              <div key={q} style={{ border: cardBorder, boxSizing: 'border-box', width: '100%' }}>
                <div style={{ background: headerBg, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontWeight: 900, fontSize: 17, color: isCurrent ? '#E8C98F' : '#000000' }}>
                    {label} {isCurrent ? '⚡ CURRENT' : isPast ? '✅ PAST' : ''}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: isCurrent ? '#ffffff' : '#000000' }}>{range}</span>
                </div>
                <div style={{
                  background: bodyBg,
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                  boxSizing: 'border-box',
                }}>
                  <div style={{ padding: '12px 14px', borderRight: isMobile ? 'none' : `1px solid ${isCurrent ? '#E8C98F' : '#000000'}`, borderBottom: isMobile ? `1px solid ${isCurrent ? '#E8C98F' : '#000000'}` : 'none', boxSizing: 'border-box', minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: lblColor, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                      EST. SELF-EMPLOYMENT INCOME TAX
                    </div>
                    <AutoFitText style={{ fontWeight: 900, color: amtColor, marginBottom: 4, width: '100%', maxWidth: '100%', minWidth: 0, whiteSpace: 'nowrap', textAlign: 'center' }} maxFontSize={22}>
                      {fmt(se)}
                    </AutoFitText>
                    <div style={{ fontSize: 11, fontWeight: 900, color: lblColor }}>
                      Due: {seDue}
                    </div>
                  </div>
                  <div style={{ padding: '12px 14px', borderRight: isMobile ? 'none' : `1px solid ${isCurrent ? '#E8C98F' : '#000000'}`, borderBottom: isMobile ? `1px solid ${isCurrent ? '#E8C98F' : '#000000'}` : 'none', boxSizing: 'border-box', minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: lblColor, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                      EST. STATE INCOME TAX
                    </div>
                    <AutoFitText style={{ fontWeight: 900, color: amtColor, marginBottom: 4, width: '100%', maxWidth: '100%', minWidth: 0, whiteSpace: 'nowrap', textAlign: 'center' }} maxFontSize={22}>
                      {fmt(state)}
                    </AutoFitText>
                    <div style={{ fontSize: 11, fontWeight: 900, color: lblColor }}>
                      Due: {stateDue}
                    </div>
                  </div>
                  <div style={{ padding: '12px 14px', boxSizing: 'border-box', minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: lblColor, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                      SALES TAX COLLECTED IN MYTOOL NOW
                    </div>
                    <AutoFitText style={{ fontWeight: 900, color: amtColor, marginBottom: 4, width: '100%', maxWidth: '100%', minWidth: 0, whiteSpace: 'nowrap', textAlign: 'center' }} maxFontSize={22}>
                      {fmt(sales)}
                    </AutoFitText>
                    <div style={{ fontSize: 11, fontWeight: 900, color: lblColor }}>
                      Varies by State
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* TAX REPORTS */}
      <div style={{ marginTop: isMobile ? 10 : 14, border: '6px solid #000000', boxShadow: 'inset 0 0 0 3px #E8761D', background: '#ffffff', padding: isMobile ? 14 : 18, boxSizing: 'border-box' }}>
        <div style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: '#000000', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 10, marginBottom: 14 }}>
          📊 TAX REPORTS — {selectedYear}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'SCHEDULE C',         type: 'schedule_c' },
            { label: 'PROFIT & LOSS',       type: 'profit_loss' },
            { label: 'QUARTERLY SALES TAX', type: 'quarterly_sales_tax' },
            { label: 'EXPENSE SUMMARY',     type: 'expenses_summary' },
            { label: 'MILEAGE SUMMARY',     type: 'mileage_summary' },
          ].map(({ label, type }) => (
            <button
              key={type}
              onClick={() => runReport(type)}
              disabled={rptLoading}
              className="jf-hover-blue"
              style={{
                background: '#000000',
                color: '#E8C98F',
                border: '3px solid #E8761D',
                fontWeight: 900,
                padding: '10px 8px',
                cursor: rptLoading ? 'wait' : 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: isMobile ? 11 : 12,
                transition: 'all 140ms ease',
                width: '100%',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {rptActive === 'quarterly_sales_tax' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ color: '#FFA500', fontWeight: 900, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>
              QUARTER:
            </span>
            <select
              value={selectedQuarter}
              onChange={(e) => {
                const q = Number(e.target.value);
                setSelectedQuarter(q);
                runReport('quarterly_sales_tax', q);
              }}
              style={{
                background: '#0b1a3a',
                color: '#FFA500',
                border: '3px solid #E8761D',
                fontWeight: 900,
                padding: '9px 16px',
                cursor: 'pointer',
                fontSize: 13,
                letterSpacing: 1,
              }}
            >
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>Q{q}</option>
              ))}
            </select>
          </div>
        )}

        {rptLoading && (
          <div style={{ color: '#FFA500', fontWeight: 900, fontSize: 15, textAlign: 'center', padding: 20 }}>
            ⏳ LOADING REPORT...
          </div>
        )}

        {rptError && !rptLoading && (
          <div style={{ border: '3px solid #c1121f', background: '#ffffff', color: '#c1121f', fontWeight: 900, padding: '12px 16px', fontSize: 14 }}>
            ⚠️ {rptError}
          </div>
        )}

        {rptData && !rptLoading && (() => {
          const t = rptData.totals || {};
          const m = rptData.meta   || {};
          const mileageRateDisplay = getReportMileageRateDisplay(rptData, rptActive);
          const mileageRateCell = mileageRateDisplay.label
            ? [['MILEAGE RATE', mileageRateDisplay.label]]
            : [];
          const cells =
            rptActive === 'schedule_c'         ? [['GROSS RECEIPTS',    money(t.gross_receipts)],   ['TOTAL EXPENSES',    money(t.total_expenses)],   ['MILEAGE DEDUCTION',   money(t.mileage_deduction)], ...mileageRateCell,             ['NET PROFIT',          money(t.net_profit)]]           :
            rptActive === 'profit_loss'         ? [['TOTAL REVENUE',     money(t.total_revenue)],    ['TOTAL EXPENSES',    money(t.total_expenses)],   ['MILEAGE DEDUCTION',   money(t.mileage_deduction)], ...mileageRateCell,             ['NET PROFIT',          money(t.net_profit)]]           :
            rptActive === 'quarterly_sales_tax' ? getQuarterlySalesTaxCells(t, m) :
            rptActive === 'expenses_summary'    ? [['# OF EXPENSES',     `${t.total_expenses}`],           ['TOTAL AMOUNT',      money(t.total_amount)]]                                                                                                                                                              :
            rptActive === 'mileage_summary'     ? [['TOTAL TRIPS',       `${t.total_trips}`],              ['TOTAL MILES',       `${Number(t.total_miles || 0).toFixed(2)}`], ...mileageRateCell,             ['DEDUCTIBLE AMOUNT',   money(t.deductible_amount)]]    :
            [];
          const rptNameLabel = { schedule_c: 'SCHEDULE C', profit_loss: 'PROFIT & LOSS', quarterly_sales_tax: 'QUARTERLY SALES TAX', expenses_summary: 'EXPENSE SUMMARY', mileage_summary: 'MILEAGE SUMMARY' };
          return (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {cells.map(([lbl, val]) => (
                  <div key={lbl} style={{ flex: '1 1 220px', background: '#0b1a3a', border: '2px solid #E8761D', padding: '12px 16px', boxSizing: 'border-box', minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: '#ffffff', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{lbl}</div>
                    <div style={{ fontSize: 'clamp(14px, 2vw, 22px)', fontWeight: 900, color: '#FFA500', width: '100%', maxWidth: '100%', minWidth: 0, whiteSpace: 'nowrap', textAlign: 'center' }}>{val}</div>
                  </div>
                ))}
              </div>
              {rptActive === 'mileage_summary' && mileageRateDisplay.breakdown.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 900, fontSize: 13, color: '#FFA500', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 6, marginBottom: 10 }}>MILEAGE RATE BREAKDOWN</div>
                  {mileageRateDisplay.breakdown.map(row => (
                    <div key={row.mileageRateId} style={{ color: '#0b1a3a', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                      {row.effectiveDate} • IRS Rate: ${Number(row.rate).toFixed(3)}/mile • Miles: {Number(row.miles).toFixed(2)} • Deduction: {money(row.deduction)}
                    </div>
                  ))}
                </div>
              )}
              {rptActive === 'mileage_summary' && getVehicleReportRows(rptData).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 900, fontSize: 13, color: '#FFA500', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 6, marginBottom: 10 }}>BY VEHICLE</div>
                  {getVehicleReportRows(rptData).map((row, index) => (
                    <div key={`${row.vehicle}-${index}`} style={{ color: '#0b1a3a', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                      {row.vehicle} • {row.status} • Trips: {row.trips} • Miles: {row.miles} • Deduction: {row.deduction}
                    </div>
                  ))}
                </div>
              )}
              {rptActive === 'quarterly_sales_tax' && Array.isArray(rptData.by_state) && rptData.by_state.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 900, fontSize: 13, color: '#FFA500', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 6, marginBottom: 10 }}>BY STATE</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          {['STATE', 'INVOICES', 'STATE TAX', 'COUNTY TAX', 'TOTAL TAX'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 900, color: '#FFA500', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, background: '#0b1a3a', borderBottom: '2px solid #E8761D' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rptData.by_state.map((row, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#0d1f3c' : '#0a1628', borderBottom: '1px solid #1a3a5c' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#ffffff' }}>{row.state_name} ({row.state_abbr})</td>
                            <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{row.invoice_count}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#FFA500' }}>{money(row.state_tax_collected)}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#FFA500' }}>{money(row.county_tax_collected)}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#FFA500' }}>{money(row.total_tax_collected)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {rptActive === 'quarterly_sales_tax' && Array.isArray(rptData.by_county) && rptData.by_county.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 900, fontSize: 13, color: '#FFA500', letterSpacing: 2, textTransform: 'uppercase', borderBottom: '2px solid #E8761D', paddingBottom: 6, marginBottom: 10 }}>BY COUNTY</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          {['STATE', 'COUNTY', 'RATE', 'INVOICES', 'STATE TAX', 'COUNTY TAX', 'TOTAL TAX'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 900, color: '#FFA500', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, background: '#0b1a3a', borderBottom: '2px solid #E8761D' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rptData.by_county.map((row, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#0d1f3c' : '#0a1628', borderBottom: '1px solid #1a3a5c' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#ffffff' }}>{row.state_abbr}</td>
                            <td style={{ padding: '8px 12px', color: '#ffffff' }}>{row.county_name}</td>
                            <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{row.rate}</td>
                            <td style={{ padding: '8px 12px', color: '#d1d5db' }}>{row.invoice_count}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#FFA500' }}>{money(row.state_tax_collected)}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#FFA500' }}>{money(row.county_tax_collected)}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 900, color: '#FFA500' }}>{money(row.total_tax_collected)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={handleRptPrint}
                  style={{
                    background: '#1a6fc4',
                    color: '#ffffff',
                    border: '3px solid #ffffff',
                    fontWeight: 900,
                    padding: '10px 20px',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: 14,
                  }}
                >
                  🖨️ PRINT {rptNameLabel[rptActive] || (rptActive || 'REPORT').toUpperCase()}
                </button>
              </div>
            </>
          );
        })()}
      </div>

      {/* CONSOLIDATED TAX DISCLAIMER */}
      <div style={{
        width: '100%',
        marginTop: isMobile ? 10 : 14,
        background: '#fff7ed',
        border: '3px solid #000000',
        padding: isMobile ? '12px 14px' : '14px 18px',
        boxSizing: 'border-box',
        color: '#000000',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 22, lineHeight: 1, marginBottom: 6 }}>⚠️</div>
        <div style={{ fontSize: 11, fontWeight: 900, lineHeight: 1.5, marginBottom: 5 }}>
          TAX ESTIMATES AND REPORTING DISCLAIMER
        </div>
        <div style={{ fontSize: 10, fontWeight: 400, lineHeight: 1.5, marginBottom: 5 }}>
          All figures are calculated from data entered and recorded in MyTool NOW. Estimated self-employment and state income tax figures are estimates, not official IRS calculations. Outside income, additional deductions, filing status, and prior-year tax history are not accounted for here.
        </div>
        <div style={{ fontSize: 10, fontWeight: 400, lineHeight: 1.5, marginBottom: 5 }}>
          Estimated self-employment and state income tax figures are based on cumulative year-to-date income, expenses, and mileage using the IRS Annualized Income Installment Method. Each quarterly estimate accounts for all prior quarters in the current year.
        </div>
        <div style={{ fontSize: 10, fontWeight: 400, lineHeight: 1.5, marginBottom: 5 }}>
          Sales Tax Collected reflects actual amounts from paid invoices recorded in MyTool NOW during each applicable quarter. Remit sales tax to the applicable state tax authority according to your state's schedule.
        </div>
        <div style={{ fontSize: 10, fontWeight: 400, lineHeight: 1.5, marginBottom: 5 }}>
          Bring these reports to your tax professional at filing time, and consult your accountant or tax professional for guidance.
        </div>
      </div>

    </div>
    </>
  );
};

export default TaxCenterUI;
